'use client';

import { FC, useState } from 'react';

import { Address, AddressDetail } from '@ravago/shared/page-data/models/elements/address.model';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';

import { useTranslations } from '../../../../providers/translation-client-context-provider';
import OrderNotFoundIllustration from '../../../illustrations/order-not-found-illustration';
import { EmptyState } from '../../../shared/empty-state/empty-state.component';

import AddressDetailFlyout from './address-detail-flyout';

interface Props {
   addresses: Array<Address>;
   getAddressDetails: (addressId: number) => Promise<AddressDetail | undefined>;
}

export const AddressList: FC<Props> = ({ addresses, getAddressDetails }) => {
   const translate = useTranslations();
   const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
   const [addressDetailId, setAddressDetailId] = useState<number | undefined>(undefined);

   const handleFlyoutOpen = (addressId: Address['id']) => {
      setAddressDetailId(addressId);
      setIsFlyoutOpen(true);
   };

   const onFlyoutClose = () => {
      setAddressDetailId(undefined);
      setIsFlyoutOpen(false);
   };

   if (addresses.length === 0) {
      return (
         <EmptyState
            data-testid="address__no-addresses-found"
            image={<OrderNotFoundIllustration />}
            title={translate('addresses.no-addresses-found')}
         />
      );
   }

   const addressList = addresses.map(({ id, name, countryName, type, line1, line2, line3, line4, line5 }) => {
      const addressLines = [line1, line2, line3, line4, line5]
         .filter(Boolean)
         .map((line, index, arr) => (index !== arr.length - 1 ? `${line}, ` : line));

      return (
         <Box key={id + type} onClick={() => handleFlyoutOpen(id)}>
            <Paper width="100%" data-testid="address__list-item" shadow>
               <Box direction="row">
                  <Typography data-testid="address__name" component="h3" variant="body-highlight">
                     {name}
                  </Typography>
                  <Badge variant="info" data-testid="address__type">
                     {translate(`addresses.types.${type === 'Legal Address' ? 'legal-address' : 'delivery-address'}`)}
                  </Badge>
               </Box>

               <Box direction="column" gap="2xs">
                  <Typography variant="body-highlight">{translate('addresses.address')}</Typography>
                  <Typography data-testid="address__lines" variant="body">
                     {addressLines} - {countryName}
                  </Typography>
               </Box>
            </Paper>
         </Box>
      );
   });

   return (
      <>
         <Box direction="column">
            <Typography variant="heading-2" component="h2">
               {translate('addresses.title')}
            </Typography>
            <Box direction="column">{addressList}</Box>
         </Box>
         {isFlyoutOpen && (
            <AddressDetailFlyout
               open={isFlyoutOpen}
               onFlyoutClose={onFlyoutClose}
               addressId={addressDetailId}
               getAddressDetails={getAddressDetails}
            />
         )}
      </>
   );
};
